import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Phone, MessageSquare, ExternalLink, Globe, Code2, ChevronRight, MessageCircle, Send, Palette, RotateCcw, Loader2, ImagePlus, Trash2 } from 'lucide-react';
import { saveBrandColor, saveOrbLabel, saveOrbImage } from '../../../api/ivrAgents.js';

/**
 * ShareLinkModal — 「語音 / 文字網頁客服」分享連結
 * 產生兩個可複製的公開網址（share-web，免安裝、瀏覽器即用）：
 *   語音 WebCall：https://ivr-web.qubby.io/voice?spaceId=<spaceId>
 *   文字客服：    https://ivr-web.qubby.io/text?spaceId=<spaceId>
 * 米色系 editorial 風（Fraunces 標題 + JetBrains Mono 網址 + terracotta accent）。
 */
const SHARE_BASE = 'https://ivr-web.qubby.io';

const overlay = { hidden: { opacity: 0 }, show: { opacity: 1 }, exit: { opacity: 0 } };
const panel = {
    hidden: { opacity: 0, y: 18, scale: 0.97 },
    show: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.07, delayChildren: 0.05 },
    },
    exit: { opacity: 0, y: 10, scale: 0.985, transition: { duration: 0.18 } },
};
const item = {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

const EMBED_SRC = `${SHARE_BASE}/embed.js`;

export function ShareLinkModal({ open, onClose, spaceId, avatarId, avatarName, currentBrandColor = '', currentOrbLabel = '', currentOrbImageUrl = '' }) {
    const { t } = useTranslation('wsModals');
    const [sdkOpen, setSdkOpen] = useState(false);
    const [brandColor, setBrandColor] = useState(isValidHex(currentBrandColor) ? currentBrandColor.toLowerCase() : BRAND_DEFAULT);
    const [colorState, setColorState] = useState('idle'); // idle | saving | saved | error
    const [orbLabel, setOrbLabel] = useState(currentOrbLabel || '');
    const [labelState, setLabelState] = useState('idle');
    const [orbImageUrl, setOrbImageUrl] = useState(currentOrbImageUrl || '');
    const [imageState, setImageState] = useState('idle'); // idle | saving | saved | error
    const voiceUrl = spaceId ? `${SHARE_BASE}/voice?spaceId=${spaceId}` : '';
    const textUrl = spaceId ? `${SHARE_BASE}/text?spaceId=${spaceId}` : '';

    // 選色即存 → avatar.brandColor（分享頁前台 + 下方連結 + 嵌入插件預設都吃這個色）。
    // 預設磚紅 → 存空字串清掉 brandColor（前台回退內建色）。
    const applyColor = async (hex) => {
        const v = (hex || '').toLowerCase();
        setBrandColor(v);
        if (!avatarId || !isValidHex(v)) return;
        setColorState('saving');
        try {
            await saveBrandColor(avatarId, v === BRAND_DEFAULT ? '' : v);
            setColorState('saved');
            setTimeout(() => setColorState('idle'), 1800);
        } catch {
            setColorState('error');
        }
    };

    // 語音球中央文字 — 失焦才存（不是每個字都打 API）。空字串 = 還原成角色名前 2 字。
    const applyOrbLabel = async () => {
        const v = orbLabel.trim();
        if (!avatarId || v === (currentOrbLabel || '').trim()) return;
        setLabelState('saving');
        try {
            await saveOrbLabel(avatarId, v);
            setLabelState('saved');
            setTimeout(() => setLabelState('idle'), 1800);
        } catch {
            setLabelState('error');
        }
    };

    // 語音球圖片 — 選檔即裁成正方形上傳；有圖就蓋住分享頁的語音球（連中央文字）。null = 移除。
    const applyOrbImage = async (file) => {
        if (!avatarId) return;
        setImageState('saving');
        try {
            const r = await saveOrbImage(avatarId, file ? await cropToSquarePng(file) : '');
            setOrbImageUrl(file ? r?.orbImageUrl || '' : '');
            setImageState('saved');
            setTimeout(() => setImageState('idle'), 1800);
        } catch {
            setImageState('error');
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    variants={overlay}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    onClick={onClose}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/40 px-5 backdrop-blur-[3px]"
                >
                    <motion.div
                        variants={panel}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-[520px] overflow-hidden rounded-[22px] bg-cream-50 shadow-[0_36px_90px_-28px_rgba(28,26,23,0.5)] ring-1 ring-cream-200"
                    >
                        {/* decorative warm wash */}
                        <div className="pointer-events-none absolute -right-20 -top-20 size-48 rounded-full bg-accent-100/45 blur-3xl" />
                        <div className="pointer-events-none absolute -left-24 bottom-0 size-40 rounded-full bg-cream-300/30 blur-3xl" />

                        {/* header */}
                        <motion.div variants={item} className="relative px-7 pt-7 pb-5">
                            <button
                                onClick={onClose}
                                title={t('common:actions.close')}
                                className="absolute right-5 top-5 inline-flex size-8 items-center justify-center rounded-full text-ink-300 transition-colors hover:bg-cream-100 hover:text-ink-900"
                            >
                                <X size={17} />
                            </button>
                            <div className="mb-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-accent-700">
                                <Globe size={12} strokeWidth={2.2} /> {t('share.badge')}
                            </div>
                            <h2 className="heading-display text-[27px] font-semibold leading-[1.1] text-ink-900">
                                {t('share.title')}
                            </h2>
                            <p className="mt-2.5 max-w-[94%] text-[13px] leading-relaxed text-ink-500">
                                {t('share.descPrefix')}{' '}
                                <span className="font-medium text-ink-700">{avatarName || t('share.thisAgent')}</span> {t('share.descSuffix')}
                                {spaceId && (
                                    <span className="ml-1.5 rounded bg-cream-100 px-1.5 py-0.5 font-mono text-[11px] text-ink-400">
                                        spaceId {spaceId}
                                    </span>
                                )}
                            </p>
                        </motion.div>

                        <div className="h-px bg-cream-200" />

                        {/* 品牌配色 — 染分享頁前台 + 下方連結 + 嵌入插件預設 */}
                        {avatarId && (
                            <motion.div variants={item} className="px-7 pt-5">
                                <div className="rounded-2xl border border-cream-200 bg-white p-4">
                                    <div className="mb-3 flex items-center justify-between">
                                        <div className="inline-flex items-center gap-2">
                                            <span className="inline-flex size-7 items-center justify-center rounded-lg bg-cream-100 text-ink-600">
                                                <Palette size={15} strokeWidth={1.8} />
                                            </span>
                                            <div>
                                                <div className="text-[13.5px] font-semibold text-ink-900">{t('share.color.title')}</div>
                                                <div className="text-[11px] leading-snug text-ink-400">{t('share.color.hint')}</div>
                                            </div>
                                        </div>
                                        {colorState === 'saving' && <span className="inline-flex items-center gap-1 text-[11px] text-ink-400"><Loader2 size={12} className="animate-spin" /> {t('share.color.saving')}</span>}
                                        {colorState === 'saved' && <span className="inline-flex items-center gap-1 text-[11px] text-success-600"><Check size={12} /> {t('share.color.saved')}</span>}
                                        {colorState === 'error' && <span className="text-[11px] text-danger-500">{t('brand.errInvalid')}</span>}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {PRESETS.map((hex) => {
                                            const active = brandColor === hex;
                                            return (
                                                <button
                                                    key={hex}
                                                    type="button"
                                                    onClick={() => applyColor(hex)}
                                                    title={hex === BRAND_DEFAULT ? t('brand.defaultTag') : hex}
                                                    className={[
                                                        'relative size-8 rounded-lg transition-all',
                                                        active ? 'ring-2 ring-ink-900 ring-offset-1 ring-offset-cream-50 scale-105' : 'ring-1 ring-ink-900/10 hover:scale-105',
                                                    ].join(' ')}
                                                    style={{ background: hex }}
                                                >
                                                    {active && <Check size={14} strokeWidth={2.6} className="absolute inset-0 m-auto text-white" />}
                                                </button>
                                            );
                                        })}
                                        {/* 自訂 hex */}
                                        <label className="relative inline-flex size-8 cursor-pointer items-center justify-center overflow-hidden rounded-lg ring-1 ring-dashed ring-ink-900/25" title={t('brand.customLabel')}>
                                            <input type="color" value={isValidHex(brandColor) ? brandColor : BRAND_DEFAULT} onChange={(e) => applyColor(e.target.value)} className="absolute -inset-1 cursor-pointer border-0 bg-transparent p-0" />
                                            <Palette size={13} className="pointer-events-none text-ink-400" />
                                        </label>
                                        <span className="ml-0.5 font-mono text-[11px] uppercase text-ink-400">{brandColor}</span>
                                        {brandColor !== BRAND_DEFAULT && (
                                            <button type="button" onClick={() => applyColor(BRAND_DEFAULT)} className="inline-flex items-center gap-1 text-[11px] text-ink-300 transition-colors hover:text-accent-600">
                                                <RotateCcw size={12} /> {t('brand.reset')}
                                            </button>
                                        )}
                                    </div>

                                    {/* 語音球中央文字 — 沒設就是角色名前 2 字 */}
                                    <div className="mt-4 border-t border-cream-100 pt-3.5">
                                        <div className="mb-2 flex items-center justify-between">
                                            <div>
                                                <div className="text-[13.5px] font-semibold text-ink-900">{t('share.orbLabel.title')}</div>
                                                <div className="text-[11px] leading-snug text-ink-400">{t('share.orbLabel.hint')}</div>
                                            </div>
                                            {labelState === 'saving' && <span className="inline-flex items-center gap-1 text-[11px] text-ink-400"><Loader2 size={12} className="animate-spin" /> {t('share.color.saving')}</span>}
                                            {labelState === 'saved' && <span className="inline-flex items-center gap-1 text-[11px] text-success-600"><Check size={12} /> {t('share.color.saved')}</span>}
                                            {labelState === 'error' && <span className="text-[11px] text-danger-500">{t('brand.errInvalid')}</span>}
                                        </div>
                                        <input
                                            type="text"
                                            value={orbLabel}
                                            maxLength={20}
                                            onChange={(e) => setOrbLabel(e.target.value)}
                                            onBlur={applyOrbLabel}
                                            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                                            placeholder={(avatarName || '').slice(0, 2) || t('share.orbLabel.placeholder')}
                                            className="w-full rounded-xl border border-cream-200 bg-cream-50/60 px-3 py-2 text-[13.5px] text-ink-900 outline-none transition-colors placeholder:text-ink-300 focus:border-accent-400 focus:bg-white"
                                        />
                                    </div>

                                    {/* 語音球圖片 — 有上傳就整顆球換成圓形圖片，中央文字不顯示 */}
                                    <div className="mt-4 border-t border-cream-100 pt-3.5">
                                        <div className="mb-2 flex items-center justify-between">
                                            <div>
                                                <div className="text-[13.5px] font-semibold text-ink-900">{t('share.orbImage.title')}</div>
                                                <div className="text-[11px] leading-snug text-ink-400">{t('share.orbImage.hint')}</div>
                                            </div>
                                            {imageState === 'saving' && <span className="inline-flex items-center gap-1 text-[11px] text-ink-400"><Loader2 size={12} className="animate-spin" /> {t('share.color.saving')}</span>}
                                            {imageState === 'saved' && <span className="inline-flex items-center gap-1 text-[11px] text-success-600"><Check size={12} /> {t('share.color.saved')}</span>}
                                            {imageState === 'error' && <span className="text-[11px] text-danger-500">{t('share.orbImage.error')}</span>}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {orbImageUrl ? (
                                                <img src={orbImageUrl} alt="" className="size-14 rounded-full bg-white object-cover ring-1 ring-cream-200" />
                                            ) : (
                                                <span className="inline-flex size-14 items-center justify-center rounded-full bg-[#232327] text-[11px] text-white/80">{(avatarName || '').slice(0, 2)}</span>
                                            )}
                                            <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-cream-200 bg-cream-50/60 px-3 py-2 text-[12.5px] text-ink-700 transition-colors hover:bg-white ${imageState === 'saving' ? 'pointer-events-none opacity-50' : ''}`}>
                                                <ImagePlus size={14} /> {t(orbImageUrl ? 'share.orbImage.replace' : 'share.orbImage.upload')}
                                                <input
                                                    id="orb-image-input"
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/webp"
                                                    className="sr-only"
                                                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) applyOrbImage(f); }}
                                                />
                                            </label>
                                            {orbImageUrl && (
                                                <button type="button" onClick={() => applyOrbImage(null)} disabled={imageState === 'saving'} className="inline-flex items-center gap-1 text-[11.5px] text-ink-400 hover:text-danger-500 disabled:opacity-50">
                                                    <Trash2 size={12} /> {t('share.orbImage.remove')}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* link cards */}
                        {!spaceId ? (
                            <motion.div variants={item} className="px-7 py-6">
                                <div className="rounded-xl bg-warn-100/50 px-4 py-3.5 text-[13px] leading-relaxed text-ink-700">
                                    {t('share.noSpaceIdPre')}<span className="font-mono text-[12px]">spaceId</span>{t('share.noSpaceIdPost')}
                                </div>
                            </motion.div>
                        ) : (
                            <div className="flex flex-col gap-3 px-7 py-6">
                                <LinkRow
                                    n="01"
                                    icon={Phone}
                                    accent
                                    label={t('share.voiceLabel')}
                                    hint={t('share.voiceHint')}
                                    url={voiceUrl}
                                />
                                <LinkRow
                                    n="02"
                                    icon={MessageSquare}
                                    label={t('share.textLabel')}
                                    hint={t('share.textHint')}
                                    url={textUrl}
                                />
                                {/* 03 — 網站嵌入 SDK：點擊展開整合說明 dialog */}
                                <motion.button
                                    type="button"
                                    variants={item}
                                    onClick={() => setSdkOpen(true)}
                                    className="group relative flex items-center gap-3 rounded-2xl border border-cream-200 bg-white p-4 text-left transition-colors hover:border-accent-300 hover:bg-accent-50/30"
                                >
                                    <span className="absolute right-4 top-3.5 font-mono text-[10px] tracking-wider text-ink-300">03</span>
                                    <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-cream-100 text-ink-600 transition-colors group-hover:bg-accent-500 group-hover:text-cream-50">
                                        <Code2 size={17} strokeWidth={1.7} />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-[14px] font-semibold text-ink-900">{t('share.sdk.label')}</span>
                                        <span className="block text-[11.5px] leading-snug text-ink-400">{t('share.sdk.hint')}</span>
                                    </span>
                                    <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-cream-100 px-2.5 py-1.5 text-[12px] font-medium text-ink-600 transition-colors group-hover:bg-accent-100 group-hover:text-accent-700">
                                        {t('share.sdk.open')} <ChevronRight size={14} />
                                    </span>
                                </motion.button>
                            </div>
                        )}
                    </motion.div>

                    {spaceId && (
                        <SdkDialog open={sdkOpen} onClose={() => setSdkOpen(false)} spaceId={spaceId} brandColor={brandColor} />
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/** 複製到剪貼簿（含 http 非安全環境 fallback，admin 有時走 http://<ip>:8082）。 */
async function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
        try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch { return false; }
}

/**
 * SdkDialog — 「網站嵌入 SDK」整合說明：
 *   - Bubble / Inline 模式 + 文字 / 語音 切換 → 即時生成 <script> 程式碼
 *   - 右側 mock 瀏覽器框「效果預覽」：看整合後 widget 長怎樣
 */
function SdkDialog({ open, onClose, spaceId, brandColor }) {
    const { t } = useTranslation('wsModals');
    const [mode, setMode] = useState('bubble'); // bubble | inline
    const [call, setCall] = useState('text');   // text | voice
    // 文字模式標題列那顆「切換到語音」電話鈕要不要給訪客看（0922 客戶回報：
    // 只想做文字客服的網站，客人按下去會跳到語音，而他們沒打算接語音）。
    // 跟 mode/call/color 一樣是純 local state —— 只影響下面產生的那段程式碼，不存後端。
    const [voiceBtn, setVoiceBtn] = useState(true);
    // 預設帶工作室設定的品牌色（沒設就 terracotta）→ 嵌入碼跟前台同色
    const [color, setColor] = useState(isValidHex(brandColor) ? brandColor.toLowerCase() : BRAND_DEFAULT);
    const [copied, setCopied] = useState(false);

    const code = buildSnippet({ spaceId, mode, call, color, voiceBtn });

    const doCopy = async () => {
        if (await copyText(code)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    variants={overlay}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    onClick={onClose}
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-900/45 px-5 backdrop-blur-[3px]"
                >
                    <motion.div
                        variants={panel}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-[760px] overflow-hidden rounded-[22px] bg-cream-50 shadow-[0_36px_90px_-28px_rgba(28,26,23,0.5)] ring-1 ring-cream-200"
                    >
                        <button
                            onClick={onClose}
                            title={t('common:actions.close')}
                            className="absolute right-5 top-5 z-10 inline-flex size-8 items-center justify-center rounded-full text-ink-300 transition-colors hover:bg-cream-100 hover:text-ink-900"
                        >
                            <X size={17} />
                        </button>

                        {/* header */}
                        <div className="px-7 pt-7 pb-4">
                            <div className="mb-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-accent-700">
                                <Code2 size={12} strokeWidth={2.2} /> SDK
                            </div>
                            <h2 className="heading-display text-[25px] font-semibold leading-[1.1] text-ink-900">
                                {t('share.sdk.title')}
                            </h2>
                            <p className="mt-2 max-w-[88%] text-[13px] leading-relaxed text-ink-500">
                                {t('share.sdk.desc')}
                            </p>
                        </div>

                        <div className="h-px bg-cream-200" />

                        <div className="grid gap-6 px-7 py-6 md:grid-cols-[1fr_300px]">
                            {/* left: controls + code */}
                            <div className="min-w-0">
                                <div className="mb-3 flex flex-wrap gap-4">
                                    <Segmented
                                        options={[
                                            { v: 'bubble', label: t('share.sdk.modeBubble') },
                                            { v: 'inline', label: t('share.sdk.modeInline') },
                                        ]}
                                        value={mode}
                                        onChange={setMode}
                                    />
                                    <Segmented
                                        options={[
                                            { v: 'text', label: t('share.sdk.callText') },
                                            { v: 'voice', label: t('share.sdk.callVoice') },
                                        ]}
                                        value={call}
                                        onChange={setCall}
                                    />
                                    {/* 文字模式限定：訪客可否從文字畫面切到語音通話（那顆電話鈕）。
                                        語音模式不顯示 —— 進去就是語音，再說「不准切語音」語意矛盾。 */}
                                    {call === 'text' && (
                                        <label className="inline-flex items-center gap-2 rounded-xl bg-cream-100 px-2.5 py-1.5">
                                            <span className="text-[12.5px] font-medium text-ink-500">{t('share.sdk.voiceBtnLabel')}</span>
                                            <Toggle on={voiceBtn} onChange={setVoiceBtn} />
                                        </label>
                                    )}
                                    {/* 品牌主色 — 同時染氣泡 + 面板 */}
                                    <label className="inline-flex items-center gap-2 rounded-xl bg-cream-100 px-2.5 py-1.5">
                                        <span className="text-[12.5px] font-medium text-ink-500">{t('share.sdk.colorLabel')}</span>
                                        <span className="relative inline-flex size-6 overflow-hidden rounded-md ring-1 ring-ink-900/10">
                                            <input
                                                type="color"
                                                value={color}
                                                onChange={(e) => setColor(e.target.value)}
                                                className="absolute -inset-1 cursor-pointer border-0 bg-transparent p-0"
                                                title={t('share.sdk.colorLabel')}
                                            />
                                        </span>
                                        {color.toLowerCase() !== BRAND_DEFAULT && (
                                            <button
                                                type="button"
                                                onClick={() => setColor(BRAND_DEFAULT)}
                                                className="font-mono text-[10px] text-ink-300 underline-offset-2 hover:text-accent-600 hover:underline"
                                            >
                                                {t('share.sdk.colorReset')}
                                            </button>
                                        )}
                                    </label>
                                </div>

                                <div className="overflow-hidden rounded-xl bg-ink-900">
                                    <div className="flex items-center justify-between border-b border-cream-50/10 px-3 py-2">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream-50/40">embed.js</span>
                                        <button
                                            onClick={doCopy}
                                            className={[
                                                'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11.5px] font-medium transition-all active:scale-95',
                                                copied ? 'bg-success-500 text-cream-50' : 'bg-cream-50/15 text-cream-50 hover:bg-cream-50/25',
                                            ].join(' ')}
                                        >
                                            {copied ? <><Check size={13} /> {t('share.sdk.copied')}</> : <><Copy size={13} /> {t('share.sdk.copyCode')}</>}
                                        </button>
                                    </div>
                                    <pre className="overflow-x-auto px-4 py-3.5 font-mono text-[12px] leading-relaxed text-cream-100"><code>{code}</code></pre>
                                </div>

                                <p className="mt-3 text-[11.5px] leading-relaxed text-ink-400">
                                    {mode === 'inline' ? t('share.sdk.inlineDesc') : t('share.sdk.bubbleDesc')}
                                    {call === 'voice' && <> · {t('share.sdk.voiceNote')}</>}
                                </p>
                                <p className="mt-1.5 text-[11.5px] leading-relaxed text-ink-400">{t('share.sdk.docNote')}</p>
                            </div>

                            {/* right: 效果預覽 mock browser */}
                            <div>
                                <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-300">
                                    {t('share.sdk.previewTitle')}
                                </div>
                                <PreviewFrame mode={mode} call={call} color={color} t={t} />
                                <p className="mt-2 text-center text-[11px] text-ink-400">
                                    {mode === 'inline' ? t('share.sdk.previewInlineHint') : t('share.sdk.previewBubbleHint')}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

const BRAND_DEFAULT = '#c84a2c';

// 選檔 → 置中裁成正方形 → 縮到 512×512 PNG dataURL（球體最大 180px，留 retina 餘裕）
async function cropToSquarePng(file, size = 512) {
    const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => rej(new Error('unreadable image'));
        i.src = URL.createObjectURL(file);
    });
    const s = Math.min(img.naturalWidth, img.naturalHeight);
    const c = document.createElement('canvas');
    c.width = c.height = size;
    c.getContext('2d').drawImage(img, (img.naturalWidth - s) / 2, (img.naturalHeight - s) / 2, s, s, 0, 0, size, size);
    URL.revokeObjectURL(img.src);
    return c.toDataURL('image/png');
}
// 品牌色票（第一個=預設磚紅）— 醫療藍綠 / 醫美玫瑰 / 科技紫 / 沉穩石板
const PRESETS = ['#c84a2c', '#2563eb', '#0d9488', '#16a34a', '#7c3aed', '#db2777', '#d97706', '#475569'];
const isValidHex = (s) => /^#[0-9a-fA-F]{6}$/.test(String(s || '').trim());

function buildSnippet({ spaceId, mode, call, color, voiceBtn = true }) {
    const lines = [`<script src="${EMBED_SRC}"`, `  data-space-id="${spaceId}"`];
    if (mode === 'inline') { lines.push('  data-mode="inline"'); lines.push('  data-target="#qubby-widget"'); }
    if (call === 'voice') lines.push('  data-call-mode="voice"');
    // 這個參數只被 TextConsole 讀（管文字畫面標題列那顆電話鈕）→ 只有文字模式寫得出來。
    // 語音模式也寫的話語意矛盾（進去就是語音，卻說不准切到語音），所以那裡不寫、UI 也不顯示。
    // 預設顯示 → 只在關掉時才寫，貼出去的那段保持最短。
    if (call === 'text' && !voiceBtn) lines.push('  data-voice-switch="0"');
    if (color && color.toLowerCase() !== BRAND_DEFAULT) lines.push(`  data-color="${color}"`);
    return lines.join('\n') + '></script>';
}

// hex → "r,g,b"（mock 預覽用，把選的色帶透明度當淺色面）
function hexToRgb(hex) {
    let h = String(hex || '').replace(/^#/, '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return '200,74,44';
    return `${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)}`;
}

function Toggle({ on, onChange }) {
    return (
        <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)}
            className="relative h-6 w-11 shrink-0 rounded-full transition"
            style={{ background: on ? BRAND_DEFAULT : '#d6d3cd' }}>
            <span className="absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow transition-transform"
                style={{ transform: on ? 'translateX(20px)' : 'none' }} />
        </button>
    );
}

function Segmented({ options, value, onChange }) {
    return (
        <div className="inline-flex rounded-xl bg-cream-100 p-1">
            {options.map((o) => (
                <button
                    key={o.v}
                    type="button"
                    onClick={() => onChange(o.v)}
                    className={[
                        'rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-all',
                        value === o.v ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-400 hover:text-ink-700',
                    ].join(' ')}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

/** Mock 瀏覽器框 — 純 CSS 示意整合後 widget 的位置與外觀，accent 元素跟著選的 color 變。 */
function PreviewFrame({ mode, call, color, t }) {
    const rgb = hexToRgb(color);
    const tint = (a) => `rgba(${rgb},${a})`; // 由選的色帶透明度當淺色面/邊框
    return (
        <div className="overflow-hidden rounded-xl border border-cream-300 bg-white shadow-sm">
            {/* fake browser chrome */}
            <div className="flex items-center gap-1.5 border-b border-cream-200 bg-cream-100 px-3 py-2">
                <span className="size-2 rounded-full bg-danger-500/60" />
                <span className="size-2 rounded-full bg-warn-500/60" />
                <span className="size-2 rounded-full bg-success-500/60" />
                <span className="ml-2 truncate rounded bg-white px-2 py-0.5 font-mono text-[9px] text-ink-300">yoursite.com</span>
            </div>
            {/* page body */}
            <div className="relative h-[230px] bg-cream-50/60 p-3">
                {/* faux content lines */}
                <div className="space-y-2">
                    <div className="h-2.5 w-2/3 rounded bg-cream-200" />
                    <div className="h-2 w-full rounded bg-cream-200/70" />
                    <div className="h-2 w-5/6 rounded bg-cream-200/70" />
                </div>

                {mode === 'inline' ? (
                    <div className="absolute inset-x-3 bottom-3 top-16 overflow-hidden rounded-lg bg-white shadow-sm" style={{ border: `1px solid ${tint(0.35)}` }}>
                        <MockPanel call={call} color={color} tint={tint} t={t} compact />
                    </div>
                ) : (
                    <>
                        {/* opened chat panel */}
                        <div className="absolute bottom-14 right-2 w-[150px] overflow-hidden rounded-lg border border-cream-200 bg-white shadow-md">
                            <MockPanel call={call} color={color} tint={tint} t={t} />
                        </div>
                        {/* launcher bubble */}
                        <div
                            className="absolute bottom-2.5 right-2.5 inline-flex size-9 items-center justify-center rounded-full text-white"
                            style={{ background: color, boxShadow: `0 8px 18px -6px ${tint(0.6)}` }}
                        >
                            <X size={15} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function MockPanel({ call, color, tint, t, compact }) {
    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center gap-1.5 border-b border-cream-200 px-2.5 py-1.5">
                <span className="size-1.5 rounded-full bg-success-500" />
                <span className="truncate text-[9px] font-semibold text-ink-700">{t('share.sdk.mockTitle')}</span>
                {call === 'voice'
                    ? <Phone size={9} className="ml-auto" style={{ color }} />
                    : <MessageCircle size={9} className="ml-auto text-ink-300" />}
            </div>
            <div className="flex flex-1 flex-col justify-end gap-1 p-2">
                {/* AI 訊息（淺色面）+ 使用者訊息（主色），示意 accent 染到對話 */}
                <div className="self-start max-w-[88%] rounded-lg rounded-tl-sm px-2 py-1 text-[8.5px] leading-snug text-ink-700" style={{ background: tint(0.12) }}>
                    {t('share.sdk.mockGreeting')}
                </div>
                <div className="self-end max-w-[70%] rounded-lg rounded-br-sm px-2 py-1 text-[8.5px] leading-snug text-white" style={{ background: color }}>
                    {t('share.sdk.mockUser')}
                </div>
            </div>
            <div className="flex items-center gap-1 border-t border-cream-200 px-2 py-1.5">
                <div className="h-3.5 flex-1 rounded-full bg-cream-100" />
                <span className="inline-flex size-4 items-center justify-center rounded-full text-white" style={{ background: color }}><Send size={8} /></span>
            </div>
        </div>
    );
}

function LinkRow({ n, icon: Icon, label, hint, url, accent }) {
    const { t } = useTranslation('wsModals');
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        let ok = false;
        if (navigator.clipboard && window.isSecureContext) {
            try { await navigator.clipboard.writeText(url); ok = true; } catch { /* fall through */ }
        }
        if (!ok) {
            // HTTP (insecure context) fallback — admin-console 有時走 http://<ip>:8082
            try {
                const ta = document.createElement('textarea');
                ta.value = url;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand('copy');
                document.body.removeChild(ta);
            } catch { /* give up silently */ }
        }
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }
    };

    return (
        <motion.div
            variants={item}
            className={[
                'group relative rounded-2xl border p-4 transition-colors',
                accent ? 'border-accent-100 bg-accent-50/40' : 'border-cream-200 bg-white',
            ].join(' ')}
        >
            <span className="absolute right-4 top-3.5 font-mono text-[10px] tracking-wider text-ink-300">{n}</span>

            <div className="mb-3 flex items-center gap-2.5">
                <span
                    className={[
                        'inline-flex size-9 items-center justify-center rounded-xl',
                        accent ? 'bg-accent-500 text-cream-50' : 'bg-cream-100 text-ink-600',
                    ].join(' ')}
                >
                    <Icon size={17} strokeWidth={1.7} />
                </span>
                <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-ink-900">{label}</div>
                    <div className="text-[11.5px] leading-snug text-ink-400">{hint}</div>
                </div>
            </div>

            <div className="flex items-stretch gap-2">
                <div className="flex min-w-0 flex-1 items-center rounded-xl border border-cream-200 bg-cream-50 px-3 py-2">
                    <span className="truncate font-mono text-[12px] text-ink-700">{url}</span>
                </div>
                <button
                    onClick={copy}
                    className={[
                        'inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3.5 text-[12px] font-medium transition-all active:scale-95',
                        copied
                            ? 'bg-success-500 text-cream-50'
                            : accent
                                ? 'bg-accent-500 text-cream-50 hover:bg-accent-700'
                                : 'bg-ink-900 text-cream-50 hover:bg-ink-700',
                    ].join(' ')}
                >
                    {copied ? <><Check size={14} /> {t('share.copied')}</> : <><Copy size={14} /> {t('share.copy')}</>}
                </button>
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    title={t('share.openNewTab')}
                    className="inline-flex shrink-0 items-center justify-center rounded-xl border border-cream-200 bg-white px-2.5 text-ink-400 transition-colors hover:border-accent-300 hover:text-accent-600"
                >
                    <ExternalLink size={15} />
                </a>
            </div>
        </motion.div>
    );
}
